import { yupResolver } from "@hookform/resolvers/yup";
import type { AxiosError } from "axios";
import Axios from "axios";
import type { ChangeEvent } from "react";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import styled from "styled-components/macro";
import useSWR from "swr";
import * as yup from "yup";
import "yup-phone";
import { endpoints } from "../../endpoints";
import {
  Flex,
  Flex1,
  Flex2,
  Form,
  FormGrid2x2,
  RadioButtonContainer,
} from "../../layout/FormLayout";
import { PageTitle } from "../../layout/portalPageLayout";
import type {
  LeadAddress,
  OptionType,
  SupportedLanguage,
  Tenant,
  TenantType,
  User,
  UserType,
  UserTypeChip,
  UUID,
} from "../../types/types";
import type { RoleSummary } from "../../types/types.PIM";
import { useCountriesList, useStatesList } from "../../util/Locations";
import { useRoutePath } from "../../util/Routing";
import {
  getCountryOption,
  getStateOption,
  getStatesInputLabel,
  getZipCodeInputLabel,
} from "../../util/location";
import {
  countryCodeMap,
  getCountryCode,
  getPhoneCodeOption,
  getPhoneCodesOptions,
  getPhoneNumber,
} from "../../util/phone";
import { strings } from "../../util/strings";
import { useBusinessUnits } from "../../util/useBusinessUnit";
import {
  convertUserToOption,
  useFormWrapper,
  useIsActylisStorefront,
  useStoreState,
  useSupportedLanguages,
} from "../../util/util";
import { useAuthContext } from "../Auth";
import {
  PrimaryButtonLargeV2,
  TertiaryButtonLargeV2,
} from "../Buttons/Buttons";
import { CheckBoxNoLabel } from "../CheckBoxes/CheckBoxes";
import type { ChipType } from "../Chips/Chips";
import { Chips } from "../Chips/Chips";
import {
  CheckBoxContainer,
  CheckBoxFinePrintLabel,
  SectionTitle,
} from "../Form/Form";
import { HeaderLeft } from "../Layout/Layout";
import { Notifications } from "../Notifications/NotificationsContext";
import { RadioButton } from "../RadioButton/RadioButton";
import { SelectBoxV2 } from "../SelectBoxV2/SelectBoxV2";
import { TextField } from "../TextFields/TextFields";
import { SearchSelectInfiniteScroll } from "../SearchSelectInfiniteScroll/SearchSelectInfiniteScroll";
import { ToggleSwitchV2 } from "../ToggleSwitch/ToggleSwitch";
import { H3 } from "../Typography/Typography";
import { TUserNotificationsPreferences } from "../UserNotifications/user_notif_util";
import { UserNotifications } from "../UserNotifications/UserNotifications";
import { spacingTokens } from "../../designTokens";

interface FormInputs {
  customer_search: OptionType;
  firstname: string;
  lastname: string;
  email_address: string;
  crm_id?: string;
  phone_number: string;
  preferred_language: OptionType<SupportedLanguage>;
  country_code: OptionType;
  user_type_chip: UserTypeChip;
  role?: OptionType<UUID>;
  quote_threshold_usd: number;
  order_threshold_usd: number;
  quote_threshold_local: number;
  order_threshold_local: number;
  quote_threshold_usd_unlimited: boolean;
  quote_threshold_default_unlimited: boolean;
  order_threshold_usd_unlimited: boolean;
  order_threshold_default_unlimited: boolean;
  leads_notification: boolean;
  leads_enable_quote_request: boolean;
  leads_enable_sample_request: boolean;
  leads_enable_registration: boolean;
  leads_enable_contact_us: boolean;
  transactions_notification: boolean;
  transactions_enable_order: boolean;
  transactions_enable_sample_request: boolean;
  transactions_enable_quote_request: boolean;
  userCheckbox: boolean;
  shippingCheckbox: boolean;
  country: OptionType;
  business_unit: OptionType;
  user_country: OptionType | null;
  street_name: string;
  address2: string | null;
  state: OptionType;
  city: string;
  postal_code: string;
  contact_first_name: string;
  contact_last_name: string;
  contact_email_address: string;
  contact_country_code: OptionType;
  contact_phone_number: string;
  point_of_contact_id: OptionType;
}

type LeadData = {
  firstname: string;
  lastname: string;
  email_address: string;
  phone_number: string;
  type?: "sample_request" | "lead";
  lead_id: string;
  existing_customer: {
    company_name: string;
    customer_id: string;
  };
  address?: LeadAddress | null;
};

type CreateNewUserProps = {
  tenantId: string;
  userTypeChips: UserTypeChip[];
  defaultUserType: UserType;
  userFormType: "tenant" | "customer_distributor" | "add_to_Existing_customer";
  leadToUserData?: LeadData;
  onSuccess: () => void;
  onCancel: () => void;
  tenantTypeToFetch: Extract<TenantType, "Buyer" | "Distributor">;
  showShippingAddress?: boolean;
  isLeadForwarded?: boolean;
  handleSkipAdding?: () => Promise<void>;
};

const CheckBoxAndTextField = styled.div`
  display: grid;
  grid-template-columns: 40% 60%;
  align-items: center;
`;

/**
 * if the storefront is not pim-only edition and the user is not admin
 */

export function roleSummaryToOption(roles: RoleSummary): OptionType<UUID>[] {
  return roles.map((role) => ({ value: role.id, label: role.name }));
}

function getUserType(role: string | undefined) {
  if (!role) {
    return null;
  } else if (role === "Admin") {
    return "Seller Admin";
  } else return "Seller Standard";
}

export function CreateNewUser({
  tenantId,
  userTypeChips,
  defaultUserType,
  leadToUserData,
  userFormType,
  onCancel,
  onSuccess,
  tenantTypeToFetch,
  showShippingAddress = false,
  isLeadForwarded = false,
  handleSkipAdding,
}: CreateNewUserProps) {
  const {
    storefront_id,
    storefront_metadata: { default_currency, edition },
  } = useStoreState();
  const [loading, setLoading] = useState(false);
  const { notifySuccess, notifyError } = useContext(Notifications);
  const {
    roleIsSomeKindOfBuyer,
    roleIsSellerAdmin,
    roleIsDistributorAdmin,
    user,
  } = useAuthContext();
  const [isRequiredQuoteUSD, setIsRequiredQuoteUSD] = useState(false);
  const [isRequiredQuoteDefault, setIsRequiredQuoteDefault] = useState(false);
  const [isRequiredOrderUSD, setIsRequiredOrderUSD] = useState(false);
  const [isRequiredOrderDefault, setIsRequiredOrderDefault] = useState(false);
  const [isRequiredUserCheckbox, setIsRequiredUserCheckbox] = useState(false);
  const [isRequiredShippingCheckbox, setIsRequiredShippingCheckbox] =
    useState(false);
  const [isRequiredExistingUser, setIsRequiredExistingUser] = useState(false);
  const [isRequiredManualUser, setIsRequiredManualUser] = useState(false);
  const [userNotifications, setUserNotifications] =
    useState<TUserNotificationsPreferences>({
      leads_enable_quote_request: true,
      leads_enable_sample_request: true,
      leads_enable_registration: true,
      leads_enable_contact_us: true,
      leads: true,
      transactions: true,
      transactions_enable_order: true,
      transactions_enable_sample_request: true,
      transactions_enable_quote_request: true,
    });
  const { supportedLanguageOptions } = useSupportedLanguages();
  const { adminPath } = useRoutePath();
  const history = useHistory();
  const [pointOfContactType, setPointOfContactType] =
    useState<string>("existing");
  const [useHqAddress, setUseHqAddress] = useState(false);
  const [isSwitchDisabled, setSwitchDisabled] = useState(false);
  const [storedCountry, setStoredCountry] = useState(
    leadToUserData?.address?.country === "USA"
      ? "US"
      : leadToUserData?.address?.country
  );
  const countries = useCountriesList();
  const business_units = useBusinessUnits();

  const foundDefaultUserChip = userTypeChips.find(
    (chip) => chip.id === defaultUserType
  );
  if (!foundDefaultUserChip) {
    console.warn("Did not find the default user type for creating a new user");
  }
  const defaultUserChip = foundDefaultUserChip || userTypeChips[0];

  // buyers don't have roles.. yet.
  const roleSelectShouldBeDisplayed =
    userFormType === "tenant" && roleIsSellerAdmin;

  const leadsPrefShouldBeDisplayed =
    ["tenant", "customer_distributor"].includes(userFormType) &&
    defaultUserType !== "Buyer Admin" &&
    (roleIsSellerAdmin || roleIsDistributorAdmin);

  const { t } = useTranslation();

  function generatePOSTBody(inputs: FormInputs) {
    const maybeUserType = getUserType(inputs?.role?.label);
    const postBody = {
      email_address: inputs.email_address,
      crm_id: inputs.crm_id,
      firstname: inputs.firstname,
      preferred_language: inputs.preferred_language.value,
      lastname: inputs.lastname,
      phone_number: `${inputs.country_code?.value}${inputs.phone_number}`,
      user_type: maybeUserType ? maybeUserType : inputs.user_type_chip.id,
      ...(inputs?.role?.value && { role_id: inputs.role.value }),
      // The following is safe to do because while 0 is a valid value these
      // values will come in as "0". We are treating null here as "unlimited".
      // (which is the default is nothing is ever set for existing users.)
      quote_threshold_usd: inputs.quote_threshold_usd || null,
      order_threshold_usd: inputs.order_threshold_usd || null,
      quote_threshold_local: inputs.quote_threshold_local || null,
      order_threshold_local: inputs.quote_threshold_local || null,
      email_notifications:
        roleSelectShouldBeDisplayed || leadsPrefShouldBeDisplayed
          ? {
              storefront_id: storefront_id,
              // leads
              leads: userNotifications.leads,
              leads_enable_quote_request:
                userNotifications.leads_enable_quote_request,
              leads_enable_sample_request:
                userNotifications.leads_enable_sample_request,
              leads_enable_registration:
                userNotifications.leads_enable_registration,
              leads_enable_contact_us:
                userNotifications.leads_enable_contact_us,
              // transactions
              transactions: userNotifications.transactions,
              transactions_enable_order:
                userNotifications.transactions_enable_order,
              transactions_enable_sample_request:
                userNotifications.transactions_enable_sample_request,
              transactions_enable_quote_request:
                userNotifications.transactions_enable_quote_request,
            }
          : undefined,
      userCheckbox: inputs.userCheckbox,
      shippingCheckbox: inputs.shippingCheckbox,
      business_unit: inputs.business_unit.value ?? null,
      country: inputs.user_country?.value ?? null,
    };

    if (leadToUserData) {
      return leadToUserData.type === "sample_request"
        ? { ...postBody, sample_request_id: leadToUserData.lead_id }
        : { ...postBody, lead_id: leadToUserData.lead_id };
    }
    return postBody;
  }

  const shouldShowShippingForm = isRequiredShippingCheckbox || isLeadForwarded;

  /**
   * Lazy evaluation below, we don't know until form submit time which fields
   * are required, or what the country code is for the phone_number.
   * For the textfields they are required if the corresponding "unlimited"
   * checkbox is unchecked.
   *
   * The calls to `typeError` handle the NaN case which is coerced from an
   * empty string. We are treating this as a required field. Even though in
   * javascript land `typeof NaN === number` and you might think this would
   * fall through to the required condition, yup feels differently.
   */
  // TODO: get this to type check without the `any`. Maybe use zod instead?
  // const CreateNewTenantUserSchema = yup.lazy<FormInputs>((formValues) => {
  const CreateNewTenantUserSchema = yup.lazy((formValues: any) => {
    return yup.object().shape({
      customer_search:
        leadToUserData && !isLeadForwarded
          ? yup
              .object()
              .shape({
                value: yup.string().required(strings(t).thisIsARequiredField),
                label: yup.string(),
              })
              .required(strings(t).thisIsARequiredField)
          : yup
              .object()
              .shape({ value: yup.string(), label: yup.string() })
              .notRequired(),

      firstname:
        isRequiredUserCheckbox && !isLeadForwarded
          ? yup.string().required(strings(t).thisIsARequiredField)
          : yup.string().notRequired(),
      lastname:
        isRequiredUserCheckbox && !isLeadForwarded
          ? yup.string().required(strings(t).thisIsARequiredField)
          : yup.string().notRequired(),
      email_address:
        isRequiredUserCheckbox && !isLeadForwarded
          ? yup.string().email().required(strings(t).thisIsARequiredField)
          : yup.string().notRequired(),

      preferred_language: yup
        .object()
        .shape({ value: yup.string(), label: yup.string() })
        .required(strings(t).thisIsARequiredField),

      phone_number:
        isRequiredUserCheckbox && !isLeadForwarded
          ? yup
              .string()
              .phone(
                countryCodeMap.get(formValues.country_code.value),
                false,
                strings(t).phoneNumberMustBeValid
              )
              .required(strings(t).thisIsARequiredField)
          : yup.string().notRequired(),

      country_code:
        isRequiredUserCheckbox && !isLeadForwarded
          ? yup
              .object()
              .shape({
                value: yup.string().required(strings(t).thisIsARequiredField),
                label: yup.string().required(strings(t).thisIsARequiredField),
              })
              .required(strings(t).thisIsARequiredField)
          : yup.string().notRequired(),

      user_type_chip: yup
        .object()
        .shape({ name: yup.string(), id: yup.string() })
        .required(strings(t).thisIsARequiredField),

      quote_threshold_usd: isRequiredQuoteUSD
        ? yup
            .number()
            .typeError(strings(t).thisIsARequiredField)
            .min(0, t("Value cannot be negative") as string)
            .required(strings(t).thisIsARequiredField)
        : yup.number().notRequired(),

      order_threshold_usd: isRequiredOrderUSD
        ? yup
            .number()
            .typeError(strings(t).thisIsARequiredField)
            .min(0, t("Value cannot be negative") as string)
            .required(strings(t).thisIsARequiredField)
        : yup.number().notRequired(),

      quote_threshold_local: isRequiredQuoteDefault
        ? yup
            .number()
            .typeError(strings(t).thisIsARequiredField)
            .min(0, t("Value cannot be negative") as string)
            .required(strings(t).thisIsARequiredField)
        : yup.number().notRequired(),

      order_threshold_local: isRequiredOrderDefault
        ? yup
            .number()
            .typeError(strings(t).thisIsARequiredField)
            .min(0, t("Value cannot be negative") as string)
            .required(strings(t).thisIsARequiredField)
        : yup.number().notRequired(),

      quote_threshold_usd_unlimited: yup.boolean().notRequired(),
      quote_threshold_default_unlimited: yup.boolean().notRequired(),
      order_threshold_usd_unlimited: yup.boolean().notRequired(),
      order_threshold_default_unlimited: yup.boolean().notRequired(),

      business_unit: yup
        .object()
        .shape({ value: yup.string(), label: yup.string() })
        .notRequired()
        .nullable(),
      user_country: yup
        .object()
        .shape({ value: yup.string(), label: yup.string() })
        .notRequired()
        .nullable(),

      country: shouldShowShippingForm
        ? yup
            .object()
            .shape({
              value: yup.string().required(strings(t).thisIsARequiredField),
              label: yup.string().required(strings(t).thisIsARequiredField),
            })
            .required(strings(t).thisIsARequiredField)
        : yup.string().notRequired(),

      street_name: shouldShowShippingForm
        ? yup.string().required(strings(t).thisIsARequiredField)
        : yup.string().notRequired(),

      city: shouldShowShippingForm
        ? yup.string().required(strings(t).thisIsARequiredField)
        : yup.string().notRequired(),

      postal_code: shouldShowShippingForm
        ? yup.string().required(strings(t).thisIsARequiredField)
        : yup.string().notRequired(),

      state: shouldShowShippingForm
        ? yup
            .object()
            .shape({
              value: yup.string().required(strings(t).thisIsARequiredField),
              label: yup.string().required(strings(t).thisIsARequiredField),
            })
            .required(strings(t).thisIsARequiredField)
        : yup.string().notRequired(),

      point_of_contact_id:
        isRequiredExistingUser && shouldShowShippingForm
          ? yup
              .object()
              .shape({
                value: yup.string().required(strings(t).thisIsARequiredField),
                label: yup.string().required(strings(t).thisIsARequiredField),
              })
              .required(strings(t).thisIsARequiredField)
          : yup.string().notRequired(),

      contact_first_name:
        isRequiredManualUser && shouldShowShippingForm
          ? yup.string().required(strings(t).thisIsARequiredField)
          : yup.string().notRequired(),

      contact_last_name:
        isRequiredManualUser && shouldShowShippingForm
          ? yup.string().required(strings(t).thisIsARequiredField)
          : yup.string().notRequired(),

      contact_email_address:
        isRequiredManualUser && shouldShowShippingForm
          ? yup.string().email().required(strings(t).thisIsARequiredField)
          : yup.string().notRequired(),

      contact_phone_number:
        isRequiredManualUser && shouldShowShippingForm && !useHqAddress
          ? yup
              .string()
              .phone(
                countryCodeMap.get(formValues.contact_country_code?.value),
                false,
                strings(t).phoneNumberMustBeValid
              )
              .required(strings(t).thisIsARequiredField)
          : yup.string().notRequired(),

      contact_country_code:
        isRequiredManualUser && shouldShowShippingForm
          ? yup
              .object()
              .shape({
                value: yup.string().required(strings(t).thisIsARequiredField),
                label: yup.string().required(strings(t).thisIsARequiredField),
              })
              .required(strings(t).thisIsARequiredField)
          : yup.string().notRequired(),

      role: roleSelectShouldBeDisplayed
        ? yup
            .object()
            .shape({
              value: yup.string().required(strings(t).thisIsARequiredField),
              label: yup.string().required(strings(t).thisIsARequiredField),
            })
            .required(strings(t).thisIsARequiredField)
        : yup.mixed().notRequired(),
    });
  });

  const {
    handleSubmit,
    register,
    control,
    setValue,
    formState,
    errors,
    clearErrors,
    watch,
  } = useFormWrapper<FormInputs>({
    resolver: yupResolver(CreateNewTenantUserSchema),
    defaultValues: {
      customer_search: undefined,
      email_address: leadToUserData?.email_address,
      firstname: leadToUserData?.firstname,
      lastname: leadToUserData?.lastname,
      phone_number: getPhoneNumber(leadToUserData?.phone_number)?.toString(),
      preferred_language: { value: "en", label: "English" },
      user_type_chip: defaultUserChip,
      quote_threshold_usd_unlimited: true,
      quote_threshold_default_unlimited: true,
      order_threshold_usd_unlimited: true,
      order_threshold_default_unlimited: true,
      leads_notification: true,
      country_code: getPhoneCodeOption(
        getCountryCode(leadToUserData?.phone_number)
      ),
      userCheckbox: true,
      shippingCheckbox: leadToUserData?.address ? true : false,
      country: { label: "", value: "" },
      business_unit: { label: "", value: "" },
      user_country: { label: "", value: "" },
      street_name: leadToUserData?.address?.address1,
      address2: leadToUserData?.address?.address2,
      state: {
        label: "",
        value: leadToUserData?.address?.state,
      },
      city: leadToUserData?.address?.city,
      postal_code: leadToUserData?.address?.postal_code,
      contact_first_name: "",
      contact_last_name: "",
      contact_email_address: "",
      contact_country_code: { value: "", label: "" },
      contact_phone_number: "",
      point_of_contact_id: { value: "", label: "" },
    },
  });
  const selectedCountry = watch("country");
  const states = useStatesList(selectedCountry?.value);
  const { data, error: rolesError } = useSWR<{ data: RoleSummary }, AxiosError>(
    `/v1/storefronts/${storefront_id}/roles`
  );

  const roles = data?.data;
  const roleOptions = roles && !rolesError ? roleSummaryToOption(roles) : [];

  const isActylisStorefront = useIsActylisStorefront();

  const watchedUserRole = watch(["role"]);

  const watchedValues = watch([
    "quote_threshold_usd_unlimited",
    "quote_threshold_default_unlimited",
    "order_threshold_usd_unlimited",
    "order_threshold_default_unlimited",
    "customer_search",
    "userCheckbox",
    "shippingCheckbox",
    "firstname",
    "lastname",
    "email_address",
    "country_code",
    "phone_number",
  ]);

  const {
    quote_threshold_usd_unlimited,
    quote_threshold_default_unlimited,
    order_threshold_usd_unlimited,
    order_threshold_default_unlimited,
    customer_search,
    firstname,
    lastname,
    email_address,
    country_code,
    phone_number,
  } = watchedValues;

  useEffect(() => {
    register({ name: "user_type_chip" }, { required: true });
  }, [register]);

  useEffect(() => {
    // Accomplish two things,
    //  1. make the input requried in the corresponding
    //  "unlimited" checkbox is unchecked
    //  2. clear the error if the user tries to submit the form with the textfield
    //  in a required state and later clicks the checkbox to make it unrequired.
    setIsRequiredQuoteUSD(
      watchedValues.quote_threshold_usd_unlimited === false
    );
    if (!isRequiredQuoteUSD && errors.quote_threshold_usd) {
      clearErrors("quote_threshold_usd");
    }

    setIsRequiredOrderUSD(
      watchedValues.order_threshold_usd_unlimited === false
    );
    if (!isRequiredOrderUSD && errors.order_threshold_usd) {
      clearErrors("order_threshold_usd");
    }

    setIsRequiredQuoteDefault(
      watchedValues.quote_threshold_default_unlimited === false
    );
    if (!isRequiredQuoteDefault && errors.quote_threshold_local) {
      clearErrors("quote_threshold_local");
    }

    setIsRequiredOrderDefault(
      watchedValues.order_threshold_default_unlimited === false
    );
    if (!isRequiredOrderDefault && errors.order_threshold_local) {
      clearErrors("order_threshold_local");
    }

    if (errors.customer_search && watchedValues?.customer_search?.value) {
      clearErrors("customer_search");
    }

    setIsRequiredUserCheckbox(watchedValues.userCheckbox);
    setIsRequiredShippingCheckbox(watchedValues.shippingCheckbox);
    setIsRequiredExistingUser(pointOfContactType === "existing");
    setIsRequiredManualUser(pointOfContactType === "manual");
  }, [
    clearErrors,
    errors.order_threshold_local,
    errors.order_threshold_usd,
    errors.quote_threshold_local,
    errors.quote_threshold_usd,
    errors.customer_search,
    isRequiredOrderDefault,
    isRequiredOrderUSD,
    isRequiredQuoteDefault,
    isRequiredQuoteUSD,
    pointOfContactType,
    watchedValues.order_threshold_default_unlimited,
    watchedValues.order_threshold_usd_unlimited,
    watchedValues.quote_threshold_default_unlimited,
    watchedValues.quote_threshold_usd_unlimited,
    watchedValues.customer_search,
    watchedValues.userCheckbox,
    watchedValues.shippingCheckbox,
  ]);

  const displaySellerLimits =
    watchedUserRole.role !== undefined &&
    edition !== "pim" &&
    watchedUserRole?.role?.label !== "Admin";

  const showLocalCurrencyLimits = default_currency !== "USD";

  const onUserTypeToggle = (userTypeChipArray: ChipType[]) => {
    setValue("user_type_chip", userTypeChipArray[0]);
  };

  const onSubmit = async (inputs: FormInputs) => {
    setLoading(true);
    const requestBody = generatePOSTBody(inputs);

    const shouldSaveLeadUser =
      (!leadToUserData?.address && !isLeadForwarded) ||
      requestBody.userCheckbox;

    const shouldSaveLeadShippingAddress =
      (leadToUserData?.address && requestBody.shippingCheckbox) ||
      isLeadForwarded;

    if (shouldSaveLeadUser) {
      try {
        const response = await Axios.post(
          endpoints.v1_storefronts_id_tenants_id_users(
            storefront_id,
            leadToUserData ? customer_search?.value : tenantId
          ),
          requestBody
        );
        if (response.status === 201) {
          setLoading(false);
          notifySuccess(t("User has been created successfully") as string);
          if (customer_search?.value) {
            history.replace(
              `${adminPath}/${
                tenantTypeToFetch === "Buyer" ? "tenants" : "distributors"
              }/${customer_search?.value}?tab=Users`
            );
          }
          onSuccess();
        }
      } catch (error) {
        setLoading(false);
        notifyError(t((error as AxiosError).message));
      }
    }

    if (shouldSaveLeadShippingAddress) {
      const isManualContact = pointOfContactType === "manual";
      const customerSearchOption = isLeadForwarded
        ? { value: user?.tenant_id, label: user?.company_name }
        : { label: customer_search?.label, value: customer_search?.value };

      const newAddress = {
        type: "Warehouse",
        company_name: customerSearchOption?.label,
        country: inputs.country?.value,
        address1: inputs.street_name,
        address2: inputs.address2,
        state: inputs.state?.value,
        city: inputs.city,
        postal_code: inputs.postal_code,
        points_of_contact: [
          isManualContact
            ? {
                poc_type: "manual",
                contact_first_name: inputs.contact_first_name,
                contact_last_name: inputs.contact_last_name,
                email_address: inputs.contact_email_address,
                phone_number: `${inputs.contact_country_code?.value}${inputs.contact_phone_number}`,
              }
            : {
                poc_type: "existing",
                point_of_contact_user_id: inputs.point_of_contact_id?.value,
              },
        ],
      };

      try {
        const lead_type =
          leadToUserData?.type === "sample_request" ? "sample_request" : "lead";
        const response = await Axios.post(
          `/v1/tenants/${customerSearchOption?.value}/addresses?lead_type=${lead_type}&lead_id=${leadToUserData?.lead_id}`,
          newAddress
        );
        if (response.status === 201) {
          setLoading(false);
          notifySuccess(t("Address has been created successfully") as string);
          if (customer_search?.value) {
            if (tenantTypeToFetch === "Distributor") {
              history.replace(`${adminPath}/organization/company-info`);
            } else {
              history.replace(
                `${adminPath}/${
                  tenantTypeToFetch === "Buyer" ? "tenants" : "distributors"
                }/${customer_search?.value}?tab=Company+Info`
              );
            }
          }
          onSuccess();
        }
      } catch (error) {
        notifyError(strings(t).submitError, { error });
      }
    }
  };

  const headingName =
    tenantTypeToFetch === "Buyer" ? "customer" : "distributor";

  const changePointOfContact = (e: React.FormEvent<HTMLSelectElement>) => {
    if (e.currentTarget.value) {
      setPointOfContactType(e.currentTarget.value);
    }
  };

  const handleSameAsAddressToggle = (e: ChangeEvent<HTMLInputElement>) => {
    const is_checked = e.currentTarget.checked;
    setUseHqAddress(is_checked);
    handleToggle(leadToUserData, is_checked);
  };

  const handleToggle = useCallback(
    (address: LeadData | undefined, isHqAddress: boolean) => {
      if (isHqAddress && address) {
        setValue("contact_first_name", firstname);
        setValue("contact_last_name", lastname);
        setValue("contact_email_address", email_address);
        setValue("contact_phone_number", phone_number);
        setValue("contact_country_code", country_code);
        clearErrors("contact_first_name");
        clearErrors("contact_last_name");
        clearErrors("contact_email_address");
        clearErrors("contact_phone_number");
        clearErrors("contact_country_code");
        setSwitchDisabled(true);
      } else {
        setValue("contact_first_name", "");
        setValue("contact_last_name", "");
        setValue("contact_email_address", "");
        setValue("contact_phone_number", "");
        setValue("contact_country_code", { label: "", value: "" });
        setSwitchDisabled(false);
      }
    },
    [
      setValue,
      clearErrors,
      firstname,
      lastname,
      email_address,
      phone_number,
      country_code,
    ]
  );

  const currentCompanyValue = customer_search?.value as string | null;

  const pageTitle = isLeadForwarded
    ? "Add as Shipping Address"
    : leadToUserData
    ? t(`Add to existing ${headingName}`)
    : t("Create new user");

  useEffect(() => {
    if (selectedCountry?.value && selectedCountry?.value !== storedCountry) {
      setStoredCountry(selectedCountry?.value);
      setValue("street_name", "");
      setValue("address2", "");
      setValue("city", "");
      setValue("postal_code", "");
      setValue("state", { label: "", value: "" });
    }
  }, [selectedCountry, storedCountry, setValue]);

  useEffect(() => {
    if (
      pointOfContactType &&
      pointOfContactType === "manual" &&
      useHqAddress &&
      leadToUserData
    ) {
      handleToggle(leadToUserData, useHqAddress);
    }
  }, [
    pointOfContactType,
    useHqAddress,
    leadToUserData,
    setValue,
    isRequiredShippingCheckbox,
    handleToggle,
  ]);

  useEffect(() => {
    if (countries.length > 0) {
      setValue(
        "country",
        getCountryOption(countries, leadToUserData?.address?.country)
      );
    }
  }, [countries, leadToUserData, setValue, isRequiredShippingCheckbox]);

  useEffect(() => {
    if (states.length > 0) {
      leadToUserData?.address?.state &&
        setValue(
          "state",
          getStateOption(states, leadToUserData?.address?.state)
        );
    }
  }, [leadToUserData, setValue, states, isRequiredShippingCheckbox]);

  return (
    <>
      <HeaderLeft>
        <PageTitle>{pageTitle}</PageTitle>
      </HeaderLeft>
      <Form noValidate onSubmit={handleSubmit(onSubmit)}>
        {leadToUserData && user && (
          <Controller
            control={control}
            name={"customer_search"}
            defaultValue={
              isLeadForwarded
                ? { value: user.tenant_id, label: user.company_name }
                : {
                    label: "",
                    value: "",
                  }
            }
            render={(props) => (
              <SearchSelectInfiniteScroll
                {...props}
                errors={errors}
                formState={formState}
                placeholder={t("Select Company")}
                baseUrl={endpoints.v1_storefronts_id_tenants_id_tenants(
                  storefront_id,
                  user.tenant_id
                )}
                params={(() => {
                  const params = new URLSearchParams();
                  params.append("order_by", "asc");
                  params.append("tenant_type", tenantTypeToFetch);
                  return params;
                })()}
                getOptions={(response: Tenant[]) =>
                  response.reduce(
                    (acc, tenant) => [
                      ...acc,
                      {
                        value: tenant.id,
                        label: tenant.name,
                      },
                    ],
                    [] as OptionType<string>[]
                  )
                }
                disabled={isLeadForwarded}
                onChange={(data: OptionType<string>) => {
                  props.onChange(data);
                  setValue("point_of_contact_id", { value: "", label: "" });
                }}
                testid={"customer_search"}
              />
            )}
          />
        )}

        {showShippingAddress && leadToUserData?.address && !isLeadForwarded && (
          <CheckBoxContainer>
            <div style={{ minWidth: "12px" }}>
              <CheckBoxNoLabel
                name="userCheckbox"
                id="userCheckbox"
                defaultChecked
                ref={register({ required: false })}
                style={{ marginRight: "5px" }}
              />
            </div>
            <CheckBoxFinePrintLabel htmlFor="userCheckbox">
              User
            </CheckBoxFinePrintLabel>
          </CheckBoxContainer>
        )}

        {isRequiredUserCheckbox && !isLeadForwarded && (
          <>
            {leadToUserData && <H3>{t("User Details")}</H3>}
            <TextField
              name="firstname"
              label={t("First Name")}
              theref={register({
                required: true,
              })}
              formState={formState}
              errors={errors}
              type="text"
            />
            <TextField
              name="lastname"
              label={t("Last Name")}
              theref={register({
                required: true,
              })}
              formState={formState}
              errors={errors}
              type="text"
            />
            <TextField
              name="email_address"
              label={t("Email")}
              theref={register({
                required: true,
              })}
              formState={formState}
              errors={errors}
              type="email"
            />
            {!roleIsSomeKindOfBuyer && (
              <TextField
                name="crm_id"
                label={t("CRM ID")}
                theref={register({
                  required: false,
                })}
                formState={formState}
                errors={errors}
                readOnly={!roleIsSellerAdmin}
                type="text"
              />
            )}
            <Flex>
              <Flex1>
                <Controller
                  as={SelectBoxV2}
                  control={control}
                  name="country_code"
                  autoComplete="countryCode"
                  placeholder={t("Country Code")}
                  id="countryCodeSelectBox"
                  options={getPhoneCodesOptions()}
                  rules={{
                    required: true,
                  }}
                  errors={errors}
                  formState={formState}
                />
              </Flex1>
              <Flex2 style={{ marginRight: 0, marginLeft: "14px" }}>
                <TextField
                  name="phone_number"
                  label={t("Phone Number")}
                  theref={register({
                    required: true,
                  })}
                  formState={formState}
                  errors={errors}
                  type="tel"
                />
              </Flex2>
            </Flex>
            <Controller
              as={SelectBoxV2}
              control={control}
              name="preferred_language"
              placeholder={t("Preferred Language")}
              options={supportedLanguageOptions}
              rules={{
                required: true,
              }}
              errors={errors}
              formState={formState}
            />
            {countries.length > 0 && (
              <Controller
                as={SelectBoxV2}
                control={control}
                name="user_country"
                autoComplete={"user_country"}
                placeholder={t("Country")}
                isClearable={true}
                options={countries}
                rules={{
                  required: false,
                }}
                errors={errors}
                formState={formState}
              />
            )}
            <Controller
              as={SelectBoxV2}
              control={control}
              name="business_unit"
              autoComplete={"business_unit"}
              placeholder={t("Business Unit")}
              isClearable={true}
              options={business_units}
              rules={{
                required: false,
              }}
              errors={errors}
              formState={formState}
            />
          </>
        )}

        {showShippingAddress && (leadToUserData?.address || isLeadForwarded) && (
          <>
            {!isLeadForwarded && (
              <CheckBoxContainer>
                <div style={{ minWidth: "12px" }}>
                  <CheckBoxNoLabel
                    name="shippingCheckbox"
                    id="shippingCheckbox"
                    defaultChecked
                    ref={register({ required: false })}
                    style={{ marginRight: "5px" }}
                  />
                </div>
                <CheckBoxFinePrintLabel htmlFor="shippingCheckbox">
                  {t("Shipping Address")}
                </CheckBoxFinePrintLabel>
              </CheckBoxContainer>
            )}

            {shouldShowShippingForm && (
              <>
                <H3>{t("Shipping Address")}</H3>
                {countries.length > 0 && (
                  <Controller
                    as={SelectBoxV2}
                    control={control}
                    name="country"
                    placeholder={t("Country")}
                    options={countries}
                    rules={{
                      required: false,
                    }}
                    errors={errors}
                    formState={formState}
                  />
                )}

                <TextField
                  name="street_name"
                  label={t("Street Name")}
                  theref={register({
                    required: true,
                  })}
                  formState={formState}
                  errors={errors}
                  type="text"
                />

                <TextField
                  name="address2"
                  label={t("Address 2 (optional)")}
                  theref={register({
                    required: true,
                  })}
                  formState={formState}
                  errors={errors}
                  type="text"
                />
                <Controller
                  as={SelectBoxV2}
                  control={control}
                  name="state"
                  placeholder={getStatesInputLabel(selectedCountry.value, t)}
                  id="portSelectBox"
                  options={states}
                  rules={{
                    required: false,
                  }}
                  errors={errors}
                  formState={formState}
                />
                <Flex>
                  <Flex2>
                    <TextField
                      name="city"
                      label={t("City")}
                      theref={register({
                        required: false,
                      })}
                      formState={formState}
                      errors={errors}
                      type="text"
                    />
                  </Flex2>
                  <Flex1>
                    <TextField
                      name="postal_code"
                      label={getZipCodeInputLabel(selectedCountry.value, t)}
                      theref={register({
                        required: false,
                      })}
                      formState={formState}
                      errors={errors}
                      type="text"
                    />
                  </Flex1>
                </Flex>

                <H3>{t("Point of Contact")}</H3>
                <RadioButtonContainer>
                  <RadioButton
                    name={"existingUser"}
                    value="existing"
                    checked={pointOfContactType === "existing"}
                    optionTitle="Existing User"
                    handleChange={changePointOfContact}
                  />
                  <RadioButton
                    name={"manualUser"}
                    value="manual"
                    checked={pointOfContactType === "manual"}
                    optionTitle="Add Manually"
                    handleChange={changePointOfContact}
                  />
                </RadioButtonContainer>

                {pointOfContactType === "existing" && (
                  <Controller
                    control={control}
                    name="point_of_contact_id"
                    defaultValue={{
                      label: "",
                      value: "",
                    }}
                    render={(props) => (
                      <SearchSelectInfiniteScroll
                        {...props}
                        placeholder={t("Select User")}
                        baseUrl={
                          currentCompanyValue
                            ? endpoints.v1_storefronts_id_tenants_id_users(
                                storefront_id,
                                currentCompanyValue
                              )
                            : null
                        }
                        params={(() => {
                          const params = new URLSearchParams();
                          params.append("order_by", "asc");
                          return params;
                        })()}
                        getOptions={(response: User[]) =>
                          response.reduce(
                            (acc, user) =>
                              user.is_active
                                ? [...acc, convertUserToOption(user)]
                                : acc,
                            [] as OptionType<string>[]
                          )
                        }
                        errors={errors}
                        formState={formState}
                      />
                    )}
                  />
                )}

                {pointOfContactType === "manual" && (
                  <>
                    <TextField
                      name="contact_first_name"
                      label={t("First Name")}
                      theref={register({
                        required: true,
                      })}
                      formState={formState}
                      errors={errors}
                      type="text"
                      readOnly={isSwitchDisabled}
                    />
                    <TextField
                      name="contact_last_name"
                      label={t("Last Name")}
                      theref={register({
                        required: true,
                      })}
                      formState={formState}
                      errors={errors}
                      type="text"
                      readOnly={isSwitchDisabled}
                    />
                    <TextField
                      name="contact_email_address"
                      autoComplete="email"
                      label={t("Email")}
                      theref={register({
                        required: true,
                      })}
                      formState={formState}
                      errors={errors}
                      type="email"
                      readOnly={isSwitchDisabled}
                    />
                    <Flex>
                      <Flex1>
                        <Controller
                          as={SelectBoxV2}
                          control={control}
                          name="contact_country_code"
                          autoComplete="countryCode"
                          placeholder={t("Country Code")}
                          id="countryCodeSelectBox"
                          options={getPhoneCodesOptions()}
                          rules={{
                            required: true,
                          }}
                          errors={errors}
                          formState={formState}
                          disabled={isSwitchDisabled}
                        />
                      </Flex1>
                      <Flex2 style={{ marginRight: 0, marginLeft: "14px" }}>
                        <TextField
                          name="contact_phone_number"
                          autoComplete="tel"
                          label={t("Phone Number")}
                          theref={register({
                            required: true,
                          })}
                          formState={formState}
                          errors={errors}
                          type="tel"
                          readOnly={isSwitchDisabled}
                        />
                      </Flex2>
                    </Flex>
                  </>
                )}

                {leadToUserData && pointOfContactType === "manual" && (
                  <div style={{ margin: `${spacingTokens.spacing_2xl} 0` }}>
                    <ToggleSwitchV2
                      label={t("Same as Lead User")}
                      name="useHqAddress"
                      ref={register({ required: false })}
                      checked={useHqAddress}
                      onChange={handleSameAsAddressToggle}
                    />
                  </div>
                )}
              </>
            )}
          </>
        )}

        {roleIsSomeKindOfBuyer && !isLeadForwarded && (
          <Chips
            name="user_type_chip"
            errors={errors}
            header={t("Account type") as string}
            chips={userTypeChips}
            handleClick={onUserTypeToggle}
            selectionMode={"single"}
            selectedChip={defaultUserChip.name}
          />
        )}
        {roleSelectShouldBeDisplayed && (
          <Controller
            as={SelectBoxV2}
            control={control}
            name="role"
            placeholder={t("Role")}
            options={roleOptions}
            rules={{
              required: true,
            }}
            errors={errors}
            formState={formState}
          />
        )}
        {displaySellerLimits && (
          <>
            <SectionTitle style={{ marginTop: "30px" }}>
              Quote Limit
            </SectionTitle>
            <FormGrid2x2>
              <CheckBoxAndTextField>
                <CheckBoxContainer>
                  <div style={{ minWidth: "12px" }}>
                    <CheckBoxNoLabel
                      name="quote_threshold_usd_unlimited"
                      id="quote_threshold_usd_unlimited"
                      defaultChecked
                      ref={register({ required: false })}
                      style={{ marginRight: "5px" }}
                    />
                  </div>
                  <CheckBoxFinePrintLabel htmlFor="quote_threshold_usd_unlimited">
                    No Limit
                  </CheckBoxFinePrintLabel>
                </CheckBoxContainer>

                <TextField
                  name="quote_threshold_usd"
                  label={t("USD Limit")}
                  theref={register({
                    required: quote_threshold_usd_unlimited === false,
                    min: 0,
                  })}
                  disabled={!!quote_threshold_usd_unlimited}
                  formState={formState}
                  errors={errors}
                  type="number"
                />
              </CheckBoxAndTextField>
              {showLocalCurrencyLimits && (
                <CheckBoxAndTextField>
                  <CheckBoxContainer>
                    <div style={{ minWidth: "12px" }}>
                      <CheckBoxNoLabel
                        name="quote_threshold_default_unlimited"
                        id="quote_threshold_default_unlimited"
                        defaultChecked
                        ref={register({ required: true })}
                        style={{ marginRight: "5px" }}
                      />
                    </div>
                    <CheckBoxFinePrintLabel htmlFor="quote_threshold_default_unlimited">
                      {t("No Limit")}
                    </CheckBoxFinePrintLabel>
                  </CheckBoxContainer>
                  <TextField
                    name="quote_threshold_local"
                    label={t(`{{default_currency}} Limit`, {
                      default_currency,
                    })}
                    theref={register({
                      required: quote_threshold_default_unlimited === false,
                      min: 0,
                    })}
                    disabled={!!quote_threshold_default_unlimited}
                    formState={formState}
                    errors={errors}
                    type="number"
                  />
                </CheckBoxAndTextField>
              )}
            </FormGrid2x2>
            <SectionTitle style={{ marginTop: "30px" }}>
              Order Limit
            </SectionTitle>
            <FormGrid2x2>
              <CheckBoxAndTextField>
                <CheckBoxContainer>
                  <div style={{ minWidth: "12px" }}>
                    <CheckBoxNoLabel
                      name="order_threshold_usd_unlimited"
                      id="order_threshold_usd_unlimited"
                      defaultChecked
                      ref={register({ required: true })}
                      style={{ marginRight: "5px" }}
                    />
                  </div>
                  <CheckBoxFinePrintLabel htmlFor="order_threshold_usd_unlimited">
                    {t("No Limit")}
                  </CheckBoxFinePrintLabel>
                </CheckBoxContainer>
                <TextField
                  name="order_threshold_usd"
                  label={t("USD Limit")}
                  theref={register({
                    required: true,
                    min: 0,
                  })}
                  disabled={!!order_threshold_usd_unlimited}
                  formState={formState}
                  errors={errors}
                  type="number"
                />
              </CheckBoxAndTextField>
              {showLocalCurrencyLimits && (
                <CheckBoxAndTextField>
                  <CheckBoxContainer>
                    <div style={{ minWidth: "12px" }}>
                      <CheckBoxNoLabel
                        name="order_threshold_default_unlimited"
                        id="order_threshold_default_unlimited"
                        defaultChecked
                        ref={register({ required: true })}
                        style={{ marginRight: "5px" }}
                      />
                    </div>
                    <CheckBoxFinePrintLabel htmlFor="order_threshold_default_unlimited">
                      {t("No Limit")}
                    </CheckBoxFinePrintLabel>
                  </CheckBoxContainer>
                  <TextField
                    name="order_threshold_local"
                    label={t(`{{default_currency}} Limit`, {
                      default_currency,
                    })}
                    theref={register({
                      required: true,
                      min: 0,
                    })}
                    disabled={!!order_threshold_default_unlimited}
                    formState={formState}
                    errors={errors}
                    type="number"
                  />
                </CheckBoxAndTextField>
              )}
            </FormGrid2x2>
          </>
        )}

        {edition !== "pim" &&
          (roleSelectShouldBeDisplayed || leadsPrefShouldBeDisplayed) && (
            <UserNotifications
              userNotifications={userNotifications}
              setUserNotifications={setUserNotifications}
              show_transactions={
                userFormType !== "customer_distributor" && !isActylisStorefront
              }
            />
          )}
        <Flex style={{ gap: spacingTokens.spacing_xl, paddingBottom: "30px" }}>
          <TertiaryButtonLargeV2 fullWidth onClick={onCancel}>
            {t("Cancel")}
          </TertiaryButtonLargeV2>
          {isLeadForwarded && (
            <TertiaryButtonLargeV2 onClick={handleSkipAdding} type="button">
              {t("Skip Adding")}
            </TertiaryButtonLargeV2>
          )}
          <PrimaryButtonLargeV2
            type="submit"
            fullWidth
            loading={loading}
            disabled={!isRequiredShippingCheckbox && !isRequiredUserCheckbox}
          >
            {leadToUserData ? t("Add") : t("Create user")}
          </PrimaryButtonLargeV2>
        </Flex>
      </Form>
    </>
  );
}
